// CORE-1750 — какой вход в повтор знакомства стоит в чате Агапеллы.
//
// QA (20.10): после «Завершить» и после «Пропустить» крупная кнопка «Пройти
// знакомство с платформой» над перепиской оставалась — и после перезагрузки, и
// в другой сессии, и в полном чате, и в компактном окне. Сервер при этом
// честно отдавал `completed` / `skipped`: кнопка его не спрашивала. С CORE-501
// она рисовалась каждому всегда («повтор без ограничений»), и состояния тура
// у неё не было вовсе — ни с сервера, ни из кэша.
//
// Решение (Ринат, 19.10): крупное приглашение — только пока тур не пройден.
// Кто хоть раз завершил или пропустил, видит на том же месте тихую ссылку
// «Пройти знакомство ещё раз»: повтор остаётся, приглашения нет.
//
// «Хоть раз» — по истории, а не по статусу. Ручной повтор перезаписывает
// статус на «идёт», закрытие посреди повтора — на «закрыт», но непройденным
// тур от этого не становится. Решает сервер: `everFinished` в ответе — тот же
// факт (OnboardingTourService.TourFacts → EverFinished в agape-backend), по
// которому тур больше сам не открывается, и кнопка с автозапуском спорить не
// должна. Своя копия правила ниже — только для бэкенда, который поля ещё не
// отдаёт (фронт и бэкенд выкатываются не одновременно).
import { OnboardingTourDto } from '../types/onboardingTour';

/**
 * Что фронт знает об истории тура этого человека.
 * - `loading` — ответа сервера ещё нет;
 * - `unavailable` — сервер не ответил, истории не знаем;
 * - `unfinished` / `finished` — сервер ответил.
 */
export type TourHistory = 'loading' | 'unavailable' | 'unfinished' | 'finished';

export type TourReplayEntry = 'banner' | 'link';

/** Завершал или пропускал ли человек тур хоть раз — как считает сервер. */
export function tourEverFinished(state: OnboardingTourDto): boolean {
    if (typeof state.everFinished === 'boolean') return state.everFinished;
    return legacyEverFinished(state);
}

/**
 * Запасной путь: ответ без `everFinished` (бэкенд до CORE-1750). Копия
 * серверного правила поле в поле — по тем же полям, что есть в ответе с
 * CORE-496. Когда поле есть, она не участвует вовсе.
 */
export function legacyEverFinished(state: OnboardingTourDto): boolean {
    return state.completedCount > 0
        || state.skippedCount > 0
        || state.lastCompletedAtUtc != null
        // Дошёл до последнего шага и закрыл или бросил — тур пройден: на этом
        // шаге Агапелла уже поблагодарила за знакомство. Сюда же строки до
        // CORE-1750, когда крестик на последнем шаге записывался «отложил».
        || ((state.status === 'closed' || state.status === 'in_progress')
            && state.totalSteps > 0
            && state.currentStep >= state.totalSteps);
}

/**
 * История после очередного ответа сервера. Пройденный тур пройденным и
 * остаётся: счётчики на сервере не убывают, а ответы на запросы, ушедшие
 * подряд (шаг, затем «завершить»), могут прийти в обратном порядке — более
 * старый не должен вернуть приглашение.
 */
export function nextTourHistory(prev: TourHistory, state: OnboardingTourDto): TourHistory {
    if (prev === 'finished') return 'finished';
    return tourEverFinished(state) ? 'finished' : 'unfinished';
}

/**
 * Что показать: крупное приглашение, тихую ссылку или пока ничего.
 *
 * Приглашение — только когда сервер прямо сказал «не пройден». Не ответил —
 * тихая ссылка: повтор доступен, а звать в тур того, кто его, возможно, уже
 * прошёл, не за что. Пока ответ едет — ничего, чтобы на глазах не менять
 * ссылку на приглашение.
 */
export function tourReplayEntry(history: TourHistory): TourReplayEntry | null {
    switch (history) {
        case 'unfinished':
            return 'banner';
        case 'finished':
        case 'unavailable':
            return 'link';
        default:
            return null;
    }
}
